/**
 * FEA draft-time DOE axis paths (ENG-2585 / ENG-2587 / ENG-2602).
 *
 * Mirrors `khorium_models.sim_params._structural_varying_paths` so the
 * frontend registry, matrix preview, and backend expander share one
 * vocabulary. Material strings stay off this set — they belong on DP
 * overrides, not `parameter_sweeps`.
 */

import type {
  EdgeLoadBc,
  LoadFaceBc,
  LoadPatchBc,
  LoadPointBc,
  PressureBc,
  StructuralStaticParams,
} from '@/api/generated/types.gen';
import type { StructuralStaticSolver } from './types';

const CONTACT_VARY_FIELDS = [
  'friction_coefficient',
  'stiffness_mpa_per_mm',
  'stick_slope_mpa_per_mm',
] as const;

const LOAD_VECTOR_AXES = [0, 1, 2] as const;

function uniqueKeys(names: readonly (string | null | undefined)[]): string[] {
  const seen = new Map<string, number>();
  for (const name of names) {
    if (name && !name.includes('.')) {
      const key = name.toLowerCase();
      seen.set(key, (seen.get(key) ?? 0) + 1);
    }
  }
  return names.filter(
    (name): name is string =>
      !!name && !name.includes('.') && seen.get(name.toLowerCase()) === 1,
  );
}

function isLoadFace(
  bc: NonNullable<StructuralStaticParams['boundary_conditions']>[number],
): bc is { type: 'load_face' } & LoadFaceBc {
  return bc.type === 'load_face';
}

function isEdgeLoad(
  bc: NonNullable<StructuralStaticParams['boundary_conditions']>[number],
): bc is { type: 'load_edge' } & EdgeLoadBc {
  return bc.type === 'load_edge';
}

function isLoadPoint(
  bc: NonNullable<StructuralStaticParams['boundary_conditions']>[number],
): bc is { type: 'load_point' } & LoadPointBc {
  return bc.type === 'load_point';
}

function isLoadPatch(
  bc: NonNullable<StructuralStaticParams['boundary_conditions']>[number],
): bc is { type: 'load_patch' } & LoadPatchBc {
  return bc.type === 'load_patch';
}

function isPressure(
  bc: NonNullable<StructuralStaticParams['boundary_conditions']>[number],
): bc is { type: 'pressure' } & PressureBc {
  return bc.type === 'pressure';
}

/** Numeric paths this structural case can carry on `parameter_sweeps`. */
export function structuralVaryingPaths(
  params: StructuralStaticParams,
): string[] {
  const paths: string[] = [];

  for (const name of uniqueKeys(
    (params.contact_pairs ?? []).map((p) => p.name),
  )) {
    for (const field of CONTACT_VARY_FIELDS) {
      paths.push(`contact_pairs.${name}.${field}`);
    }
  }
  for (const name of uniqueKeys(
    (params.bolt_shrink_preloads ?? []).map((b) => b.name),
  )) {
    paths.push(`bolt_shrink_preloads.${name}.force_n`);
  }
  for (const name of uniqueKeys(
    (params.bolt_preloads ?? []).map((b) => b.name),
  )) {
    paths.push(`bolt_preloads.${name}.force_n`);
  }
  for (const name of uniqueKeys(
    (params.tied_interfaces ?? []).map((t) => t.name),
  )) {
    paths.push(`tied_interfaces.${name}.position_tolerance_mm`);
  }

  const loadFaceIds = new Set(
    (params.boundary_conditions ?? [])
      .filter(isLoadFace)
      .map((bc) => bc.face_id),
  );
  const targets = (params.boundary_conditions ?? []).flatMap((bc) => {
    if (isLoadFace(bc)) return [bc.face_id];
    if (isEdgeLoad(bc)) return [bc.edge_id];
    if (isLoadPoint(bc) && !loadFaceIds.has(bc.face_id)) return [bc.face_id];
    return [];
  });
  for (const target of uniqueKeys(targets)) {
    for (const axis of LOAD_VECTOR_AXES) {
      paths.push(`boundary_conditions.${target}.vector_n.${axis}`);
    }
  }

  for (const name of uniqueKeys(
    (params.boundary_conditions ?? []).filter(isLoadPatch).map((bc) => bc.name),
  )) {
    for (const axis of LOAD_VECTOR_AXES) {
      paths.push(`boundary_conditions.${name}.vector_n.${axis}`);
    }
  }

  const faceKeyed = new Map<
    string,
    NonNullable<StructuralStaticParams['boundary_conditions']>
  >();
  for (const bc of params.boundary_conditions ?? []) {
    if (isLoadFace(bc) || isLoadPoint(bc) || isPressure(bc)) {
      const list = faceKeyed.get(bc.face_id) ?? [];
      list.push(bc);
      faceKeyed.set(bc.face_id, list);
    }
  }
  for (const [faceId, onFace] of faceKeyed) {
    if (
      faceId &&
      !faceId.includes('.') &&
      onFace.length === 1 &&
      isPressure(onFace[0])
    ) {
      paths.push(`boundary_conditions.${faceId}.pressure_MPa`);
    }
  }

  const gravityCount = (params.boundary_conditions ?? []).filter(
    (bc) => bc.type === 'gravity',
  ).length;
  if (gravityCount === 1) {
    paths.push('boundary_conditions.gravity.magnitude_m_s2');
  }
  const centrifugalCount = (params.boundary_conditions ?? []).filter(
    (bc) => bc.type === 'centrifugal',
  ).length;
  if (centrifugalCount === 1) {
    paths.push('boundary_conditions.centrifugal.rpm');
  }

  return paths.sort();
}

/**
 * Drop `parameter_sweeps` keys that no longer resolve on this case's live
 * contacts / bolts / loads (rename or delete). Orphan keys fail
 * `StructuralStaticParams` validation and are invisible in `FeaSweepAxes`
 * because `getSweepableFields` only lists current `structuralVaryingPaths`.
 */
export function pruneStructuralParameterSweeps(
  params: StructuralStaticParams,
): StructuralStaticSolver {
  // OpenAPI marks `template` optional on the params object; the draft union
  // requires the literal so UpdateStudyRequest / SolverSettings stay happy.
  const withTemplate: StructuralStaticSolver = {
    ...params,
    template: 'structural_static',
  };
  const sweeps = withTemplate.parameter_sweeps;
  if (!sweeps || Object.keys(sweeps).length === 0) return withTemplate;
  const allowed = new Set(structuralVaryingPaths(withTemplate));
  let changed = false;
  const next: NonNullable<StructuralStaticParams['parameter_sweeps']> = {};
  for (const [path, value] of Object.entries(sweeps)) {
    if (allowed.has(path)) {
      next[path] = value;
    } else {
      changed = true;
    }
  }
  if (!changed) return withTemplate;
  return { ...withTemplate, parameter_sweeps: next };
}

/**
 * Read the seed scalar at a structural sweep path. Returns null when the
 * path does not resolve on this case (e.g. contact removed after a sweep
 * was authored).
 */
export function readStructuralSeed(
  params: StructuralStaticParams,
  path: string,
): number | null {
  const parts = path.split('.');
  if (parts[0] === 'contact_pairs' && parts.length === 3) {
    const [, name, field] = parts;
    const pair = (params.contact_pairs ?? []).find((p) => p.name === name);
    if (!pair) return null;
    const value = pair[field as (typeof CONTACT_VARY_FIELDS)[number]];
    return typeof value === 'number' ? value : null;
  }
  if (
    (parts[0] === 'bolt_shrink_preloads' || parts[0] === 'bolt_preloads') &&
    parts.length === 3 &&
    parts[2] === 'force_n'
  ) {
    const bolts =
      parts[0] === 'bolt_shrink_preloads'
        ? (params.bolt_shrink_preloads ?? [])
        : (params.bolt_preloads ?? []);
    const bolt = bolts.find((b) => b.name === parts[1]);
    return bolt ? bolt.force_n : null;
  }
  if (
    parts[0] === 'tied_interfaces' &&
    parts.length === 3 &&
    parts[2] === 'position_tolerance_mm'
  ) {
    const tie = (params.tied_interfaces ?? []).find((t) => t.name === parts[1]);
    return tie ? (tie.position_tolerance_mm ?? null) : null;
  }
  if (
    parts[0] === 'boundary_conditions' &&
    parts.length === 3 &&
    parts[2] === 'pressure_MPa'
  ) {
    const target = parts[1];
    for (const bc of params.boundary_conditions ?? []) {
      if (isPressure(bc) && bc.face_id === target) {
        return bc.pressure_MPa;
      }
    }
    return null;
  }
  if (
    parts[0] === 'boundary_conditions' &&
    parts.length === 4 &&
    parts[2] === 'vector_n'
  ) {
    const axis = Number(parts[3]);
    if (!Number.isInteger(axis) || axis < 0 || axis > 2) return null;
    const target = parts[1];
    for (const bc of params.boundary_conditions ?? []) {
      if (isLoadFace(bc) && bc.face_id === target) {
        return bc.vector_n[axis as 0 | 1 | 2] ?? null;
      }
      if (isEdgeLoad(bc) && bc.edge_id === target) {
        return bc.vector_n[axis as 0 | 1 | 2] ?? null;
      }
      if (isLoadPoint(bc) && bc.face_id === target) {
        return bc.vector_n[axis as 0 | 1 | 2] ?? null;
      }
      if (isLoadPatch(bc) && bc.name === target) {
        return bc.vector_n[axis as 0 | 1 | 2] ?? null;
      }
    }
  }
  if (
    parts[0] === 'boundary_conditions' &&
    parts.length === 3 &&
    parts[1] === 'gravity' &&
    parts[2] === 'magnitude_m_s2'
  ) {
    const gravity = (params.boundary_conditions ?? []).find(
      (bc) => bc.type === 'gravity',
    );
    return gravity && 'magnitude_m_s2' in gravity
      ? (gravity.magnitude_m_s2 ?? null)
      : null;
  }
  if (
    parts[0] === 'boundary_conditions' &&
    parts.length === 3 &&
    parts[1] === 'centrifugal' &&
    parts[2] === 'rpm'
  ) {
    const centrifugal = (params.boundary_conditions ?? []).find(
      (bc) => bc.type === 'centrifugal',
    );
    return centrifugal && 'rpm' in centrifugal
      ? (centrifugal.rpm ?? null)
      : null;
  }
  return null;
}

/** Wire path for one force-vector component on a face/edge/patch load. */
export function loadVectorSweepPath(target: string, axis: 0 | 1 | 2): string {
  return `boundary_conditions.${target}.vector_n.${axis}`;
}

export function pressureSweepPath(faceId: string): string {
  return `boundary_conditions.${faceId}.pressure_MPa`;
}
